import { useEffect, useState } from "react";
import type { Appointment } from "../../types/garage/planning";
import type { OrdreStatus, OrdreIntervention } from "types/ordre";
import { isAPrevoir, ORDRE_FLAG_CONFIG, ORDRE_FLAGS } from "types/ordre";
import { formatTime } from "@utils/timeGrid";
import { ordresApi } from "@services/api/ordres";
import { vehiclesApi } from "@services/api/vehicles";
import { STATUS_CHIP_STYLES } from "./dropdown/OrdreStatusDropdown";

// Interventions, modèle du véhicule et dates de l'ordre ne sont pas sur
// l'enregistrement léger du rendez-vous : la bulle les charge à la volée au
// survol et les garde par ordre_id pour que les survols suivants soient
// instantanés. Reprend le rendu "Interventions (N)" d'OrdreHoverPanel.
//
// Les dates comptent même quand le magnet jumeau est introuvable : la liste des
// rendez-vous passée à la bulle est celle du jour (ou de la semaine) affiché, et
// une restitution prévue trois jours plus tard n'en fait pas partie — la ligne
// « Restitution » disparaissait alors purement et simplement.
type OrdreExtras = {
  interventions: OrdreIntervention[];
  modele: string;
  /** Secondes epoch, telles que l'ordre les porte. */
  reception?: number;
  restitution?: number;
};
const extrasCache = new Map<string, OrdreExtras>();

// Véhicule(s) mis à disposition (location, prêt, vélo) : lus sur les réservations de
// l'ordre — la source de vérité, un ordre peut en enchaîner plusieurs — et gardés par
// ordre_id comme le reste. Demande garage 2026-10-01 : le voir sans ouvrir la fiche.
type Mobility = { label: string; vehicle: string; start: string; end: string };
const mobilityCache = new Map<string, Mobility[]>();
const USAGE_LABEL: Record<string, string> = { location: "Location", pret: "Prêt", velos: "Vélo" };

/**
 * Black hover box shown over a planning appointment.
 * Displays client / plate / dates and, when the appointment is linked to an
 * ordre, a clickable status chip that opens the status menu (via onEditStatus).
 *
 * Rendered as a direct child of the appointment cell: positioning reads
 * `el.parentElement` to anchor itself beside that cell.
 */
export default function AppointmentTooltip({
  appt,
  appointments,
  status,
  fallbackDate,
  onEditStatus,
  onMouseEnter,
  onMouseLeave,
}: {
  appt: Appointment;
  appointments: Appointment[];
  status?: OrdreStatus;
  fallbackDate?: string;
  onEditStatus: (anchor: DOMRect) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const thisDate = appt.date || fallbackDate || "";
  const fmtDate = (d: string) =>
    new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });

  // Chargement différé de l'ordre lié (null = pas encore chargé) : tout vient de
  // la même requête.
  const oid = appt.ordre_id;
  const [extras, setExtras] = useState<OrdreExtras | null>(
    () => (oid ? extrasCache.get(oid) ?? null : null),
  );
  useEffect(() => {
    if (!oid || extrasCache.has(oid)) return;
    let alive = true;
    ordresApi
      .get(oid)
      .then((ordre) => {
        const found: OrdreExtras = {
          interventions: ordre.interventions || [],
          modele: (ordre.modele || "").trim(),
          reception: ordre.reception_date?.seconds,
          restitution: ordre.restitution?.seconds,
        };
        extrasCache.set(oid, found);
        if (alive) setExtras(found);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [oid]);
  const [mobilities, setMobilities] = useState<Mobility[] | null>(
    () => (oid ? mobilityCache.get(oid) ?? null : null),
  );
  useEffect(() => {
    if (!oid || mobilityCache.has(oid)) return;
    let alive = true;
    (async () => {
      const reservations = await vehiclesApi.listReservations({ ordre_id: oid });
      const vehicles = await Promise.all(
        reservations.map((r) => (r.vehicleId ? vehiclesApi.get(r.vehicleId).catch(() => null) : Promise.resolve(null))),
      );
      const short = (d?: string) => (d ? d.slice(8, 10) + "/" + d.slice(5, 7) : "");
      const found: Mobility[] = reservations
        .map((r, i) => {
          const v = vehicles[i];
          const name = v ? (v.name || [v.marque, v.modele].filter(Boolean).join(" ")) : "";
          return {
            label: (v && USAGE_LABEL[v.usage as string]) || "Véhicule",
            vehicle: [name, v?.plate].filter(Boolean).join(" · ") || "Véhicule à attribuer",
            start: [short(r.startDate), r.startHour != null ? formatTime(r.startHour) : ""].filter(Boolean).join(" "),
            end: [short(r.endDate), r.returnHour != null ? formatTime(r.returnHour) : ""].filter(Boolean).join(" "),
            _sort: `${r.startDate || ""}${String(r.startHour ?? 0).padStart(5, "0")}`,
          };
        })
        .sort((a, b) => a._sort.localeCompare(b._sort))
        .map(({ _sort, ...m }) => m);
      mobilityCache.set(oid, found);
      if (alive) setMobilities(found);
    })().catch(() => {});
    return () => {
      alive = false;
    };
  }, [oid]);

  const interventions = extras ? extras.interventions : null;
  const modele = extras ? extras.modele : "";

  const pair = appt.ordre_id ? appointments.find((a) => a.ordre_id === appt.ordre_id && a.id !== appt.id) : null;
  const receptionAppt = appt.type === "reception" ? appt : pair;
  const restitutionAppt = appt.type === "restitution" ? appt : pair;

  // Le magnet donne le créneau (début — fin) ; l'ordre, lui, ne porte qu'un
  // instant, mais il reste connu quand le magnet est hors du jour affiché.
  const fmtSlot = (a: Appointment) =>
    `${fmtDate(a.date || thisDate)} ${formatTime(a.startHour)} — ${formatTime(a.startHour + a.durationHours)}`;
  const fmtStamp = (seconds: number) => {
    const d = new Date(seconds * 1000);
    return `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })} ${formatTime(d.getHours() + d.getMinutes() / 60)}`;
  };
  const receptionLine = receptionAppt
    ? fmtSlot(receptionAppt)
    : extras?.reception
      ? fmtStamp(extras.reception)
      : null;
  const restitutionLine = restitutionAppt
    ? fmtSlot(restitutionAppt)
    : extras?.restitution
      ? fmtStamp(extras.restitution)
      : null;

  const chip = status ? STATUS_CHIP_STYLES[status] : null;
  // Drapeaux speciaux de l'ordre, du plus grave au moins grave. Le bord colore
  // de l'evenement dit qu'il y en a un ; la bulle dit lequel.
  const apptFlags = ORDRE_FLAGS.filter((f) => (appt.flags || []).includes(f));

  return (
    <div
      data-appt-tooltip
      className="fixed z-50 bg-neutral-800 text-white rounded-lg px-3 py-2"
      style={{ whiteSpace: "nowrap", minWidth: 140, maxWidth: 320 }}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      ref={(el) => {
        if (!el) return;
        const parent = el.parentElement;
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        const tipW = el.offsetWidth;
        const tipH = el.offsetHeight;
        // Keep a comfortable gap from every screen edge — the box must never hug
        // or overflow a border (esp. a tall interventions list near the bottom).
        const M = 16;
        // Place the tooltip flush against the event (1px overlap) instead of with a
        // gap: an 8px gap can sit over a neighbouring event, so moving the pointer
        // toward the tooltip would land on that event and steal/hide the hover.
        const fitsRight = rect.right + tipW <= window.innerWidth - M;
        let left = fitsRight ? rect.right - 1 : rect.left - tipW + 1;
        left = Math.min(Math.max(left, M), window.innerWidth - tipW - M);
        let top = Math.min(rect.top, window.innerHeight - tipH - M);
        top = Math.max(top, M);
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
      }}
    >
      <div className="text-h4 font-medium">{appt.clientName}</div>
      {modele && <div className="text-h5 text-neutral-200">{modele}</div>}
      {appt.plate && <div className="text-h5 text-neutral-300">{appt.plate}</div>}
      {appt.num && <div className="text-h5 text-neutral-300">OR n°{appt.num}</div>}

      {receptionLine && (
        <div className="text-h5 text-neutral-300">Réception : {receptionLine}</div>
      )}
      {restitutionLine && (
        <div className="text-h5 text-neutral-300">Restitution : {restitutionLine}</div>
      )}
      {!receptionLine && !restitutionLine && (
        <div className="text-h5 text-neutral-300">{fmtSlot(appt)}</div>
      )}

      {mobilities && mobilities.length > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-700" style={{ whiteSpace: "normal" }}>
          <div className="text-h5 text-neutral-400 font-medium mb-1">
            Véhicule{mobilities.length > 1 ? "s" : ""} mis à disposition
          </div>
          <div className="flex flex-col gap-1">
            {mobilities.map((m, i) => (
              <div key={i} className="flex flex-col">
                <span className="text-h5 text-white">
                  <span className="text-neutral-400">{m.label} : </span>{m.vehicle}
                </span>
                {(m.start || m.end) && (
                  <span className="text-h5 text-neutral-400">
                    {m.start}{m.end && m.end !== m.start ? ` → ${m.end}` : ""}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {oid && interventions !== null && (
        <div className="mt-2 pt-2 border-t border-neutral-700" style={{ whiteSpace: "normal" }}>
          <div className="text-h5 text-neutral-400 font-medium mb-1">
            Interventions ({interventions.length})
          </div>
          {interventions.length === 0 ? (
            <div className="text-h5 text-neutral-500 italic">Aucune intervention</div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {interventions.slice(0, 8).map((it) => {
                const aPrevoir = isAPrevoir(it);
                return (
                  <div key={it.id} className="flex flex-row items-baseline justify-between gap-2">
                    <span className={`text-h5 truncate ${aPrevoir ? "text-amber-300/80 line-through decoration-amber-400/50" : "text-white"}`}>
                      {it.label}{aPrevoir ? " · à prévoir" : ""}
                    </span>
                    {it.estimated_hours ? (
                      <span className="text-h5 text-neutral-400 flex-shrink-0">{+Number(it.estimated_hours || 0).toFixed(2)}h</span>
                    ) : null}
                  </div>
                );
              })}
              {interventions.length > 8 && (
                <div className="text-h5 text-neutral-500">+{interventions.length - 8} autre(s)</div>
              )}
            </div>
          )}
        </div>
      )}

      {apptFlags.length > 0 && (
        <div className="flex flex-row flex-wrap gap-1 mt-2">
          {apptFlags.map((flag) => {
            const cfg = ORDRE_FLAG_CONFIG[flag];
            return (
              <span
                key={flag}
                className="font-medium"
                style={{ background: cfg.bg, color: cfg.color, borderRadius: 5, padding: "2px 8px", fontSize: 11, lineHeight: "16px" }}
              >
                {cfg.label}
              </span>
            );
          })}
        </div>
      )}

      {chip && status ? (
        <div className="flex flex-col gap-1 mt-2">
          <span className="text-h5 text-neutral-400">Statut de l'OR</span>
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onEditStatus((e.currentTarget as HTMLElement).getBoundingClientRect());
            }}
            className="flex flex-row items-center gap-1.5 font-medium cursor-pointer transition-opacity hover:opacity-80 w-fit"
            style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "3px 10px", fontSize: 12, lineHeight: "16px" }}
          >
            {chip.label}
            <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
              <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      ) : (
        <div className="text-h5 text-neutral-400 mt-1">Cliquer pour ouvrir</div>
      )}
    </div>
  );
}
